import axios from 'axios'
import type { UserProfile, UserWithProfile } from '@/types/user'
import { useAuth } from '@/composables/useAuth'
import type { Session } from '@/types/client'
import { readGuestPrefsBeforeLogin } from "@/utils/loadGuestPrefs"
import { useModalStore } from "@/store/modules/modal"

import { environment } from '@/environments/environment'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { bufferLog } from '@/utils/debugBuffer'

const client = axios.create({
  baseURL:`${environment.API_CLIENT}`,
  withCredentials: true,
})

client.interceptors.request.use(async (config) => {
  if (Capacitor.isNativePlatform()) {
    const { value: token } = await Preferences.get({ key: 'jwt' })
    if (token) {
      config.headers['X-JWT-Token'] = token
    }
  }
  return config
})

function askToLoadPreferences(guestPrefs: UserProfile, newUserId: number, newUsername: string) {
  const modal = useModalStore()

  modal.open({
    title: "Load Guest Preferences?",
    message: "We found guest settings on this device. Do you want to load them into your new account?",
    confirmText: "Load Profile",
    cancelText: "Skip",
    onConfirm: () => loadPreferencesIntoAccount(guestPrefs, newUserId, newUsername),
  })
}

async function loadPreferencesIntoAccount(guestPrefs: UserProfile, newUserId: number, newUsername: string) {
  const prefsToSave = {
      ...guestPrefs,
      userId: newUserId,
      username: newUsername
    }
  await client.put('/user-profile/save', prefsToSave)

  const { checkLogin } = useAuth()
  await checkLogin()
}

export async function checkLogin(): Promise<UserWithProfile | null> {
  try {
    const res = await client.get<UserWithProfile>('/user/check')
    return res.data
  } catch {
    return null
  }
}

export async function getSessions(): Promise<Session[]> {
  try {
    const res = await client.get('/auth/sessions')
    return res.data
  } catch (err) {
    console.error('Failed to fetch sessions:', err)
    throw err
  }
}

export async function logoutSession(sessionId: number) {
  await client.delete(`/auth/sessions/${sessionId}`)
}

export async function logoutAllDevices() {
  await client.post('/auth/logout/all')
  const { checkLogin } = useAuth()
  checkLogin()
}

export async function logout() {
  await client.post('/auth/logout')
  const { checkLogin } = useAuth()
  checkLogin()
}

export async function qrLogin(token: string) {
  const response = await client.get(`/auth/qr/${token}`)
  const { checkLogin } = useAuth()

  if (Capacitor.isNativePlatform()) {
    const jwt = response.data?.jwt || response.data?.jwtToken;
    if (jwt) {
      await Preferences.set({ key: 'jwt', value: jwt })
      await bufferLog('[AuthService] QR Login Success');
    }
  }

  return await checkLogin()
}

export async function requestCode(email: string, purpose: 'login' | 'signup' | 'link' | 'qrLogin', recaptchaToken?: string, username?: string, userId?: number) {
  const response = await client.post('/auth/code/request', { email, purpose, recaptchaToken, username, userId })
  if (purpose == 'qrLogin') {
    return response.data.token;
  }
}

export async function verifyCode(
  email: string,
  code: string,
  purpose: "login" | "signup" | "link",
  recaptchaToken?: string
) {

  const { prefs: guestPrefs, hasPrefs } = readGuestPrefsBeforeLogin()

  const response = await client.post("/auth/code/verify", { email, code, purpose, recaptchaToken })

  const { checkLogin } = useAuth()

  if (Capacitor.isNativePlatform() && purpose !== 'link') {
    const token = response.data?.jwt
    if (!token) throw new Error('No JWT returned from server')
    await Preferences.set({ key: 'jwt', value: token })
  }

  const user = await checkLogin()
  const isProfileEmpty = user && (!user.hiddenTemplates || user.hiddenTemplates.length === 0) && (!user.included || user.included.length === 0) && (!user.excluded || user.excluded.length === 0)

  if (user && hasPrefs && guestPrefs && (purpose === "signup" || (purpose === "login" && isProfileEmpty))) {
    askToLoadPreferences(guestPrefs, user.userId, user.username)
  }
}

export async function loginWithGoogle(credential: string, recaptchaToken?: string) {
  const { prefs: guestPrefs, hasPrefs } = readGuestPrefsBeforeLogin()
  const response = await client.post('/auth/google/login', { credential, recaptchaToken })
  const { checkLogin } = useAuth()

  if (Capacitor.isNativePlatform()) {
    const token = response.data?.jwt
    if (!token) throw new Error('No JWT returned from server')
    await Preferences.set({ key: 'jwt', value: token })
  }

  const user = await checkLogin()
  const isProfileEmpty = user && (!user.hiddenTemplates || user.hiddenTemplates.length === 0) && (!user.included || user.included.length === 0) && (!user.excluded || user.excluded.length === 0)

  if (user && hasPrefs && guestPrefs && isProfileEmpty) {
    askToLoadPreferences(guestPrefs, user.userId, user.username)
  }
}

export async function signupGoogle(credential: string, recaptchaToken?: string) {
  const { prefs: guestPrefs, hasPrefs } = readGuestPrefsBeforeLogin()

  const response = await client.post("/auth/google/signup", { credential, recaptchaToken })
  const { checkLogin } = useAuth()

  if (Capacitor.isNativePlatform()) {
    const token = response.data?.jwt
    if (!token) throw new Error('No JWT returned from server')
    await Preferences.set({ key: 'jwt', value: token })
  }

  const user = await checkLogin()

  if (user && hasPrefs && guestPrefs) {
    askToLoadPreferences(guestPrefs, user.userId, user.username)
  }
}

export async function linkWithGoogle(credential: string, recaptchaToken?: string) {
  await client.post('/auth/google/link', { credential, recaptchaToken })
  const { checkLogin } = useAuth()
  await checkLogin()
}

export async function loginWithApple(credential: string, recaptchaToken?: string) {
  const { prefs: guestPrefs, hasPrefs } = readGuestPrefsBeforeLogin()
  const response = await client.post('/auth/apple/login', { credential, recaptchaToken })
  const { checkLogin } = useAuth()

  if (Capacitor.isNativePlatform()) {
    const token = response.data?.jwt
    if (!token) throw new Error('No JWT returned from server')
    await Preferences.set({ key: 'jwt', value: token })
  }

  const user = await checkLogin()
  const isProfileEmpty = user && (!user.hiddenTemplates || user.hiddenTemplates.length === 0) && (!user.included || user.included.length === 0) && (!user.excluded || user.excluded.length === 0)

  if (user && hasPrefs && guestPrefs && isProfileEmpty) {
    askToLoadPreferences(guestPrefs, user.userId, user.username)
  }
}

export async function signupApple(credential: string, recaptchaToken?: string) {
  const { prefs: guestPrefs, hasPrefs } = readGuestPrefsBeforeLogin()

  const response = await client.post("/auth/apple/signup", { credential, recaptchaToken })
  const { checkLogin } = useAuth()

  if (Capacitor.isNativePlatform()) {
    const token = response.data?.jwt
    if (!token) throw new Error('No JWT returned from server')
    await Preferences.set({ key: 'jwt', value: token })
  }

  const user = await checkLogin()

  if (user && hasPrefs && guestPrefs) {
    askToLoadPreferences(guestPrefs, user.userId, user.username)
  }
}

export async function linkWithApple(credential: string, recaptchaToken?: string) {
  await client.post('/auth/apple/link', { credential, recaptchaToken })
  const { checkLogin } = useAuth()
  await checkLogin()
}